import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
    LayoutDashboard, Building2, BedDouble, CalendarCheck, LogIn, LogOut, Users,
    Sparkles, UtensilsCrossed, Package, ShoppingCart, DollarSign, UserCog,
    FileBarChart, Settings, CreditCard, PartyPopper, Wrench, HeartHandshake,
    Globe, ShieldCheck, Bell, User, Wallet, ChefHat,
} from "lucide-react";

const NAV = [
    {
        title: "Overview",
        items: [
            { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: [] },
        ],
    },
    {
        title: "Front Office",
        items: [
            { to: "/reservations", label: "Reservations", icon: CalendarCheck, roles: ["front_desk", "reservation", "general_manager", "hotel_owner"] },
            { to: "/checkin", label: "Check-In / Out", icon: LogIn, roles: ["front_desk", "reservation", "general_manager", "hotel_owner"] },
            { to: "/guests", label: "Guests", icon: Users, roles: [] },
        ],
    },
    {
        title: "Operations",
        items: [
            { to: "/rooms", label: "Rooms", icon: BedDouble, roles: [] },
            { to: "/housekeeping", label: "Housekeeping", icon: Sparkles, roles: [] },
            { to: "/restaurant", label: "Restaurant POS", icon: UtensilsCrossed, roles: ["restaurant_manager", "kitchen", "cashier", "general_manager", "hotel_owner"] },
            { to: "/recipes", label: "Recipes", icon: ChefHat, roles: ["restaurant_manager", "kitchen", "general_manager", "hotel_owner"] },
            { to: "/banquet", label: "Banquet & Events", icon: PartyPopper, roles: ["restaurant_manager", "general_manager", "hotel_owner"] },
            { to: "/maintenance", label: "Maintenance", icon: Wrench, roles: ["maintenance", "general_manager", "hotel_owner"] },
        ],
    },
    {
        title: "Supply Chain",
        items: [
            { to: "/inventory", label: "Inventory", icon: Package, roles: ["store_manager", "general_manager", "hotel_owner", "accountant"] },
            { to: "/purchase", label: "Purchase & Vendors", icon: ShoppingCart, roles: ["store_manager", "general_manager", "hotel_owner", "accountant"] },
        ],
    },
    {
        title: "Business",
        items: [
            { to: "/finance", label: "Finance", icon: DollarSign, roles: ["accountant", "general_manager", "hotel_owner", "cashier"] },
            { to: "/payroll", label: "Payroll", icon: Wallet, roles: ["hr", "accountant", "general_manager", "hotel_owner"] },
            { to: "/hr", label: "HR & Attendance", icon: UserCog, roles: ["hr", "general_manager", "hotel_owner", "accountant"] },
            { to: "/reports", label: "Reports", icon: FileBarChart, roles: ["general_manager", "hotel_owner", "accountant"] },
            { to: "/payments", label: "Payments", icon: CreditCard, roles: ["cashier", "accountant", "general_manager", "hotel_owner"] },
        ],
    },
    {
        title: "Growth",
        items: [
            { to: "/crm", label: "CRM & Feedback", icon: HeartHandshake, roles: ["general_manager", "hotel_owner", "accountant"] },
            { to: "/ota", label: "Channel Manager", icon: Globe, roles: ["general_manager", "hotel_owner"] },
            { to: "/notifications", label: "Notifications", icon: Bell, roles: ["general_manager", "hotel_owner", "accountant"] },
        ],
    },
    {
        title: "Admin",
        items: [
            { to: "/hotels", label: "Hotels", icon: Building2, roles: ["super_admin", "hotel_owner"] },
            { to: "/users", label: "Users & Roles", icon: Settings, roles: ["super_admin", "hotel_owner", "general_manager", "hr"] },
            { to: "/audit-logs", label: "Audit Logs", icon: ShieldCheck, roles: ["super_admin", "hotel_owner", "general_manager"] },
            { to: "/profile", label: "Profile & 2FA", icon: User, roles: [] },
        ],
    },
];

export default function Sidebar() {
    const { user, logout } = useAuth();
    const role = user?.role;
    const isSuper = role === "super_admin";

    return (
        <aside className="w-60 shrink-0 bg-[#0A1C3E] text-slate-300 min-h-screen flex flex-col border-r border-slate-800" data-testid="sidebar">
            <div className="px-5 py-6 border-b border-white/5">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-sm bg-[#C5B08A] flex items-center justify-center">
                        <span className="text-[#0A1C3E] font-bold text-sm font-display">M</span>
                    </div>
                    <div>
                        <div className="text-white font-display font-bold tracking-tight text-sm">MERIDIAN</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Hotel ERP</div>
                    </div>
                </div>
            </div>

            <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
                {NAV.map((section) => {
                    const visible = section.items.filter(
                        (i) => isSuper || !i.roles.length || i.roles.includes(role),
                    );
                    if (!visible.length) return null;
                    return (
                        <div key={section.title}>
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 px-3 mb-2">
                                {section.title}
                            </div>
                            <div className="space-y-0.5">
                                {visible.map((item) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        end={item.to === "/"}
                                        className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                                    >
                                        <item.icon className="w-4 h-4" />
                                        <span>{item.label}</span>
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </nav>

            <div className="border-t border-white/5 px-4 py-4">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#C5B08A]/20 flex items-center justify-center text-[#C5B08A] text-xs font-semibold">
                        {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-xs text-white truncate">{user?.name}</div>
                        <div className="text-[10px] text-slate-400 uppercase tracking-wider">{user?.role?.replace(/_/g, " ")}</div>
                    </div>
                    <button onClick={logout} data-testid="logout-button" className="text-slate-400 hover:text-white p-1">
                        <LogOut className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </aside>
    );
}
